import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useWsNavigate } from "../../hooks/useWsNavigate";
import { scenariosAPI, metricAnnotationsAPI } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { usePageTitle } from "../../contexts/PageTitleContext";
import AudioWaveformPlayer from "../../components/AudioWaveformPlayer";
import ConversationTypeBadge from "../../components/ConversationTypeBadge";
import {
  ArrowLeft,
  Tag,
  Plus,
  X,
  RefreshCw,
  Bug,
} from "lucide-react";
import AnnotateMetricModal from "../../components/metrics/AnnotateMetricModal";
import DebugDrawer from "../../components/DebugDrawer";
import RunConfigPanel from "../../components/RunConfigPanel";
import useMetricPolling from "../../hooks/useMetricPolling";
import {
  StandardMetricsPanel,
  CustomLlmMetricsSection,
  CallInsightsPanel,
  TranscriptPanel,
  formatDuration,
} from "../../components/CallAnalysis";

// ─── Computing phrases (cycling) ──────────────────────────────────────────────

const CALL_PHRASES = [
  "Conversation is happening…",
  "Test bot is talking to the agent…",
  "Listening in on the call…",
  "Exchanging words with the agent…",
  "Putting the agent through its paces…",
  "Checking if the agent keeps its cool…",
  "Almost done chatting…",
];

const ANALYSIS_PHRASES = [
  "Crunching audio waveforms…",
  "Untangling speaker voices…",
  "Measuring response gaps…",
  "Counting interruptions…",
  "Chasing milliseconds…",
  "Timing the silences…",
  "Evaluating custom metrics…",
  "Whispering to the analyser…",
  "Almost there…",
];

const CyclingText = ({ phrases, className = "" }) => {
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % phrases.length), 1800);
    return () => clearInterval(t);
  }, [phrases]);
  return (
    <span key={idx} className={`transition-opacity duration-300 ${className}`}>
      {phrases[idx]}
    </span>
  );
};

// ─── Shimmer skeleton ─────────────────────────────────────────────────────────

const Shimmer = ({ className = "" }) => (
  <div
    className={`bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-[shimmer_1.4s_ease-in-out_infinite] rounded ${className}`}
  />
);

// ─── Metrics in-progress skeleton ────────────────────────────────────────────

const MetricsSkeleton = ({ isCallRunning }) => (
  <div className="space-y-4">
    {/* Banner */}
    <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 flex items-center gap-3">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
      </span>
      <p className="text-sm text-blue-700 font-medium">
        {isCallRunning ? (
          <CyclingText phrases={CALL_PHRASES} />
        ) : (
          <CyclingText phrases={ANALYSIS_PHRASES} />
        )}
      </p>
    </div>

    {/* Latency chart skeleton */}
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <Shimmer className="h-4 w-32" />
        <Shimmer className="h-3 w-24" />
      </div>
      <Shimmer className="h-40 w-full" />
      <div className="flex gap-3 mt-3 justify-center">
        {[64, 72, 56, 68, 60].map((w, i) => (
          <Shimmer key={i} className={`h-3 w-${w < 64 ? 14 : 16}`} />
        ))}
      </div>
    </div>

    {/* Event cards skeleton */}
    {[0, 1, 2].map((i) => (
      <div key={i} className="border border-border rounded-xl p-4 bg-card">
        <div className="flex items-center justify-between">
          <Shimmer className="h-4 w-40" />
          <Shimmer className="h-3 w-16" />
        </div>
      </div>
    ))}

    {/* LLM metrics skeleton */}
    <div className="bg-card border border-border rounded-xl p-4">
      <Shimmer className="h-4 w-32 mb-4" />
      <div className="flex flex-col gap-3">
        {[0, 1].map((i) => (
          <div key={i} className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <Shimmer className="h-3.5 w-36" />
              <Shimmer className="h-5 w-14 rounded-full" />
            </div>
            <Shimmer className="h-3 w-4/5 mt-2" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Main component ─────────────────────────────────────────────────────────

const RunResults = () => {
  const { batchId, runId } = useParams();
  const navigate = useWsNavigate();
  const { user, currentWorkspace } = useAuth();
  const { setPageTitle } = usePageTitle();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [seekTo, setSeekTo] = useState(null);
  const [showTagInput, setShowTagInput] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [rerunMetricsLoading, setRerunMetricsLoading] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [annotateModal, setAnnotateModal] = useState(null);

  const isRunFailed = (data) => data?.status === "failed";

  const isRecordingPending = (data) =>
    !isRunFailed(data) &&
    (!data?.recording_url || data.recording_url.startsWith("pending://"));

  useEffect(() => {
    setPageTitle("Run Results");
    return () => setPageTitle(null);
  }, [setPageTitle]);

  useEffect(() => {
    if (user && currentWorkspace && runId) fetchRun();
    return () => stopPolling();
    // eslint-disable-next-line
  }, [user, currentWorkspace, runId]);

  const hasMetricsPending = useCallback((data) =>
    (data.standard_metrics || []).some(
      (m) => m.status === "pending" || m.status === "processing",
    ) ||
    (data.evaluations || []).some(
      (m) => m.status === "pending" || m.status === "processing",
    ), []);

  const isStillPending = useCallback((data) =>
    data.status === "pending" ||
    data.status === "running" ||
    isRecordingPending(data) ||
    hasMetricsPending(data), [hasMetricsPending]);

  const fetchRunData = useCallback(async () => {
    const response = await scenariosAPI.getRun(batchId, runId);
    return response?.data || null;
  }, [batchId, runId]);

  const { updateData, startPolling, stopPolling, setMetricsPending, refreshRecordingUrl } = useMetricPolling(
    fetchRunData,
    setRun,
    {
      recordingUrlKey: "recording_url",
      isPending: isStillPending,
    }
  );

  const fetchRun = async (isInitial = true) => {
    if (isInitial) setLoading(true);
    try {
      const data = await fetchRunData();
      if (data) {
        updateData(data);
        startPolling(data);
      }
    } catch (err) {
      setError("Failed to load run.");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const handleRemoveTag = (tag) => {
    scenariosAPI.removeRunTag(batchId, runId, tag).then(() => fetchRun(false));
  };

  const handleAddTag = (tags) => {
    scenariosAPI.addRunTag(batchId, runId, tags).then(() => fetchRun(false));
  };

  const handleRerunMetrics = async (metricIds = null) => {
    setRerunMetricsLoading(true);
    try {
      // Immediately set all metrics to pending state in UI
      setMetricsPending(["standard_metrics", "evaluations", "system_llm_metrics"]);

      await scenariosAPI.rerunMetrics(batchId, runId, metricIds);

      // Start polling — don't fetch immediately as server might not have pending state yet
      // Use current run data with pending metrics to drive polling
      setRun((prev) => {
        if (prev) startPolling(prev);
        return prev;
      });
    } catch (err) {
      console.error("Failed to rerun metrics", err);
    } finally {
      setRerunMetricsLoading(false);
    }
  };

  // Rerun a single metric (from the per-metric kebab menu) on this run.
  const handleRerunSingleMetric = (metricId) => {
    if (metricId) handleRerunMetrics([metricId]);
  };

  const handleTimestampClick = (ms) =>
    setSeekTo({ seconds: ms / 1000, id: Date.now() });

  const handleMarkIncorrect = (m) => {
    setAnnotateModal({
      metricId: m.metric_id,
      metricName: m.metric_name,
      resultType: m.result_type || "boolean",
      resultTypeConfig: m.result_type_config,
      measuredValue: m.result_data?.value ?? (m.result_data?.metric_result || m.metric_result)?.[0]?.metric_value,
    });
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
        Loading run…
      </div>
    );
  if (error || !run)
    return (
      <div className="flex items-center justify-center py-20 text-sm text-red-500">
        {error || "Run not found."}
      </div>
    );

  const hasStandardMetrics = (run.standard_metrics || []).length > 0;
  const callInFlight = run.status === "running" || run.status === "pending";
  const isWebCall = run.voice_call_mode === "web_call";
  const hasStereoRecording = isWebCall || !!run.recording_url;
  // Which channel is the AI comes from the backend (Run#recording_ai_channel), derived
  // from durable call provenance — not the recording URL, which becomes our S3 after
  // re-host and would flip the labels. Defaults to 0 for older payloads.
  const aiChannel = run.ai_channel ?? 0;
  const webCallChannelLabel = hasStereoRecording
    ? { ai_channel: aiChannel, user_channel: aiChannel === 1 ? 0 : 1, confidence: 1.0 }
    : null;
  const allStandardPending =
    hasStandardMetrics &&
    (run.standard_metrics || []).every(
      (m) => m.status === "pending" || m.status === "processing",
    );
  const hasEvaluations = (run.evaluations || []).length > 0;
  const allEvalsPending =
    hasEvaluations &&
    (run.evaluations || []).every(
      (m) => m.status === "pending" || m.status === "processing",
    );
  const isTextMode = run.voice_call_mode === "text";
  const showStandardMetrics = !callInFlight && hasStandardMetrics && !isTextMode;
  const showEvaluations = !callInFlight && hasEvaluations;
  const showSkeleton = callInFlight && !hasStandardMetrics && !hasEvaluations;
  const analysisFailed = run.status === "failed";

  const STATUS_COLORS = {
    completed: "bg-emerald-100 text-emerald-700",
    running: "bg-blue-100 text-blue-700",
    failed: "bg-red-100 text-red-700",
    pending: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="px-4 pb-8">
      {/* Back */}
      <button
        onClick={() => navigate(`/batch-run/${batchId}`)}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4 mt-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Batch
      </button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            {run.scenario_name || `Run #${run.id}`}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {run.batch_name && <span>{run.batch_name} · </span>}
            {(run.last_run_at || run.created_at) && (
              <span>
                {run.last_run_at ? "Last run " : "Created "}
                {new Date(run.last_run_at || run.created_at).toLocaleString(
                  "en-US",
                  {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {run.duration && (
            <span className="px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
              {formatDuration(run.duration)}
            </span>
          )}
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[run.status] || STATUS_COLORS.pending}`}
          >
            {run.status || "pending"}
          </span>
          <span
            className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              run.call_success === true
                ? "bg-emerald-50 text-emerald-700"
                : run.call_success === false
                ? "bg-red-50 text-red-700"
                : run.status === "error" || run.status === "failed"
                ? "bg-red-50 text-red-600"
                : run.status === "pending"
                ? "bg-muted text-muted-foreground"
                : "bg-amber-50 text-amber-700"
            }`}
          >
            {run.call_success === true
              ? "Pass"
              : run.call_success === false
              ? "Fail"
              : run.status === "error" || run.status === "failed"
              ? "Error"
              : run.status === "pending"
              ? "Pending"
              : "Evaluating"}
          </span>
          {!isTextMode && run.call_connection_status && run.call_connection_status !== "pending" && (
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
              run.call_connection_status === "answered" ? "bg-green-100 text-green-700" :
              run.call_connection_status === "not_answered" ? "bg-amber-100 text-amber-700" :
              "bg-red-100 text-red-700"
            }`}>
              {run.call_connection_status.replace(/_/g, " ")}
            </span>
          )}
          {run.status === "failed" && run.error_message && (
            <span className="text-sm text-red-600">{run.error_message}</span>
          )}
          {(run.status === "completed" || run.status === "failed") && (
            <button
              onClick={() => handleRerunMetrics()}
              disabled={rerunMetricsLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${rerunMetricsLoading ? "animate-spin" : ""}`}
              />
              {rerunMetricsLoading ? "Re-running…" : "Rerun Metrics"}
            </button>
          )}
          {user?.roles?.includes("super_admin") && (
            <button
              onClick={() => setDebugOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors"
              title="Debug this run (super admin)"
            >
              <Bug className="h-3.5 w-3.5" />
              Debug
            </button>
          )}
        </div>
      </div>

      {debugOpen && (
        <DebugDrawer
          runId={runId}
          targetLabel={`Run #${runId}`}
          onClose={() => setDebugOpen(false)}
        />
      )}

      {/* Summary */}
      {run.summary && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 mb-6">
          <p className="text-sm font-semibold text-blue-900 mb-1">
            Evaluation Summary
          </p>
          <p className="text-sm text-blue-800 leading-relaxed">{run.summary}</p>
        </div>
      )}

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ height: "calc(100vh - 180px)" }}>
        {/* ── Left: Analysis (scrollable) ── */}
        <div className="space-y-4 overflow-y-auto pr-2">
          {/* Skeleton: call in-flight or everything still pending */}
          {showSkeleton && (
            <MetricsSkeleton isCallRunning={callInFlight} />
          )}

          {/* Empty state */}
          {!callInFlight &&
            !hasStandardMetrics &&
            !hasEvaluations && (
              <div className="bg-card border border-border rounded-xl p-4 text-center text-sm text-muted-foreground">
                No metrics configured for this scenario.
              </div>
            )}

          {/* Standard metrics (latency, interruptions, silence, voice, WPM) */}
          {showStandardMetrics && (
            <StandardMetricsPanel
              standardMetrics={run.standard_metrics || []}
              analysisFailed={analysisFailed}
              onTimestampClick={handleTimestampClick}
            />
          )}

          {/* Audio metrics still processing banner */}
          {!isTextMode && !callInFlight && hasStandardMetrics && allStandardPending && showEvaluations && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-3 flex items-center gap-3">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" />
              </span>
              <p className="text-sm text-blue-700 font-medium">Audio analysis in progress…</p>
            </div>
          )}

          {/* Custom LLM Metrics */}
          {showEvaluations && (
            <CustomLlmMetricsSection
              metrics={run.evaluations}
              onSeek={(t) => setSeekTo(t)}
              onMarkIncorrect={handleMarkIncorrect}
              onRerunMetric={handleRerunSingleMetric}
            />
          )}

          <CallInsightsPanel insights={run.llm_insights} />

          {/* Run info */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-4">
              Run Info
            </h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block">Scenario</span>
                <span className="font-medium">{run.scenario_name || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Batch</span>
                <span className="font-medium">{run.batch_name || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Duration</span>
                <span className="font-medium">
                  {formatDuration(run.duration)}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">Status</span>
                <span className="font-medium capitalize">
                  {run.status || "—"}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Type</span>
                <ConversationTypeBadge mode={run.voice_call_mode} />
              </div>
              {run.ended_by && (
                <div>
                  <span className="text-xs text-muted-foreground block">Ended By</span>
                  <span className="font-medium">
                    {run.ended_by.replace(/_/g, " ")}
                  </span>
                </div>
              )}
              {run.ended_reason &&
                (() => {
                  const NATURAL = [
                    "end_call_action",
                    "natural_conclusion",
                    "natural",
                  ];
                  const LABELS = {
                    agent_timeout: "Agent did not respond",
                    connection_failed: "Could not connect",
                    connection_lost: "Connection lost",
                    max_duration: "Max duration reached",
                    timed_out: "Timed out (no callback)",
                  };
                  const abnormal = !NATURAL.includes(run.ended_reason);
                  return (
                    <div>
                      <span className="text-xs text-muted-foreground block">Ended Reason</span>
                      <span
                        className={`font-medium ${abnormal ? "text-amber-700" : ""}`}
                      >
                        {LABELS[run.ended_reason] ||
                          run.ended_reason.replace(/[-_]/g, " ")}
                      </span>
                    </div>
                  );
                })()}
            </div>
          </div>

          <RunConfigPanel config={run.metadata?.run_config} />

          {/* Tags */}
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground">Tags</h3>
              {!showTagInput && (
                <button
                  onClick={() => setShowTagInput(true)}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  <Plus className="h-3.5 w-3.5" /> Add tag
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(run.tags || []).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-0.5 text-indigo-500 hover:text-indigo-800"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {(run.tags || []).length === 0 && !showTagInput && (
                <span className="text-xs text-muted-foreground italic">No tags</span>
              )}
            </div>
            {showTagInput && (
              <div className="flex gap-2 mt-3">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      const tags = newTag
                        .split(",")
                        .map((t) => t.trim())
                        .filter(Boolean);
                      if (tags.length) {
                        handleAddTag(tags);
                        setNewTag("");
                        setShowTagInput(false);
                      }
                    }
                  }}
                  placeholder="Tags, comma separated"
                  className="flex-1 px-3 py-1.5 border border-input bg-background rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  autoFocus
                />
                <button
                  onClick={() => {
                    const tags = newTag
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean);
                    if (tags.length) {
                      handleAddTag(tags);
                      setNewTag("");
                      setShowTagInput(false);
                    }
                  }}
                  disabled={!newTag.trim()}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40"
                >
                  Add
                </button>
                <button
                  onClick={() => {
                    setShowTagInput(false);
                    setNewTag("");
                  }}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:bg-muted/50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Waveform + Transcript (sticky) ── */}
        <div className="flex flex-col gap-4 sticky top-0 h-full min-h-0">
          {run.voice_call_mode === "text" ? null : run.status ===
              "running" || run.status === "pending" ? (
            <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center justify-center gap-3 text-sm shrink-0 min-h-[160px]">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
              </span>
              <div className="font-medium text-foreground">Call is on-going</div>
              <div className="text-xs text-muted-foreground">
                The recording will appear here once the call ends.
              </div>
            </div>
          ) : isRunFailed(run) ? (
            <>
              <div className="bg-card border border-red-100 rounded-xl p-4 text-sm text-foreground shrink-0">
                <div className="font-medium text-red-700 mb-1">
                  {run.recording_url
                    ? "Call failed — recording of what happened below"
                    : "Call failed — no recording available"}
                </div>
                {run.ended_reason && (
                  <div className="text-muted-foreground">Reason: <span className="font-mono text-xs">{run.ended_reason}</span></div>
                )}
                {(run.evaluations || []).find((e) => e.error_message)?.error_message && (
                  <div className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap break-words">
                    {(run.evaluations || []).find((e) => e.error_message).error_message}
                  </div>
                )}
              </div>
              {/* A failed call still has a recording of the part that happened —
                  show it so the failure can be heard, not just read. */}
              {run.recording_url && !run.recording_url.startsWith("pending://") && (
                <div className="bg-card border border-border rounded-xl p-4 shrink-0">
                  <AudioWaveformPlayer
                    audioUrl={run.recording_url}
                    fileName={
                      run.scenario_name
                        ? `${run.scenario_name}.wav`
                        : "recording.wav"
                    }
                    onTimeUpdate={setCurrentTime}
                    seekTo={seekTo}
                    onRecordingUrlExpired={refreshRecordingUrl}
                    channelLabel={webCallChannelLabel}
                    aiLabel={isWebCall ? "Real Agent" : "AI Assistant"}
                    userLabel={isWebCall ? "Test Agent" : "User"}
                  />
                </div>
              )}
            </>
          ) : isRecordingPending(run) ? (
            <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 text-sm text-muted-foreground shrink-0">
              <svg
                className="animate-spin h-4 w-4 text-indigo-500 shrink-0"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
              Recording is being uploaded… it will appear here automatically.
            </div>
          ) : run.recording_url ? (
            <div className="bg-card border border-border rounded-xl p-4 shrink-0">
              <AudioWaveformPlayer
                audioUrl={run.recording_url}
                fileName={
                  run.scenario_name
                    ? `${run.scenario_name}.wav`
                    : "recording.wav"
                }
                onTimeUpdate={setCurrentTime}
                seekTo={seekTo}
                onRecordingUrlExpired={refreshRecordingUrl}
                channelLabel={webCallChannelLabel}
                aiLabel={isWebCall ? "Real Agent" : "AI Assistant"}
                userLabel={isWebCall ? "Test Agent" : "User"}
              />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-4 text-center text-sm text-muted-foreground">
              No recording available yet
            </div>
          )}

          <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col min-h-0 flex-1">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between shrink-0">
              <h3 className="text-sm font-semibold text-foreground">
                Transcript
              </h3>
              {(run.transcript || []).length > 0 ? (
                <span className="text-xs text-muted-foreground">
                  {run.transcript.length} turns
                </span>
              ) : run.status === "running" || run.status === "pending" ? (
                <span className="flex items-center gap-1.5 text-xs text-indigo-500">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-indigo-500" />
                  </span>
                  Live
                </span>
              ) : null}
            </div>
            <div className="overflow-y-auto flex-1">
              {(run.status === "running" || run.status === "pending") &&
              (run.transcript || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full py-12 text-center text-sm text-muted-foreground">
                  <p>The transcript will be available once the call ends.</p>
                </div>
              ) : (
                <TranscriptPanel
                  transcript={run.transcript}
                  currentTime={currentTime}
                  onSeek={(sec) => setSeekTo({ seconds: sec, id: Date.now() })}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Annotate Metric Modal */}
      {annotateModal && (
        <AnnotateMetricModal
          visible={true}
          onClose={() => setAnnotateModal(null)}
          onSubmit={async ({ expected_value, explanation }) => {
            await metricAnnotationsAPI.create(currentWorkspace.id, {
              metric_id: annotateModal.metricId,
              annotatable_type: "Run",
              annotatable_id: runId,
              agent_id: run?.metadata?.agent_id,
              measured_value: { value: annotateModal.measuredValue },
              expected_value,
              explanation,
            });
            setRun((prev) => ({
              ...prev,
              evaluations: (prev.evaluations || []).map((ev) =>
                ev.metric_id === annotateModal.metricId
                  ? { ...ev, status: "under_investigation" }
                  : ev
              ),
            }));
          }}
          metricName={annotateModal.metricName}
          resultType={annotateModal.resultType}
          resultTypeConfig={annotateModal.resultTypeConfig}
          measuredValue={annotateModal.measuredValue}
        />
      )}
    </div>
  );
};

export default RunResults;
